import type { ResolvedCollection } from '@rstore/shared';
import type { Store } from '#imports';

/**
 * Recursively unwraps `{ data: [...] }` envelopes from nested to-many relations.
 *
 * Walks the data structure alongside the collection's normalizedRelations.
 * For each field that is a to-many relation (many === true), if the value
 * is an object with a `data` array property, it unwraps to just the array.
 *
 * @example
 * // Before (from backend)
 * { id: 1, articles: { data: [{ id: 2 }] } }
 *
 * // After (in frontend)
 * { id: 1, articles: [{ id: 2 }] }
 */
export function unwrapNestedDataEnvelopes<T>(
	data: T,
	collection: ResolvedCollection,
	store: Store,
): T {
	if (data === null || data === undefined || typeof data !== 'object') {
		return data;
	}

	if (Array.isArray(data)) {
		return data.map((item) => unwrapNestedDataEnvelopes(item, collection, store)) as T;
	}

	const result: Record<string, unknown> = {};

	for (const key in data) {
		const value = (data as Record<string, unknown>)[key];
		const relation = collection.normalizedRelations?.[key];

		if (relation?.many && isDataEnvelope(value)) {
			// Unwrap the envelope and recurse into items
			const targetCollection = getTargetCollection(relation, store);
			const items = value.data;

			result[key] = targetCollection
				? items.map((item: unknown) => unwrapNestedDataEnvelopes(item, targetCollection, store))
				: items;
		}
		else if (relation && !relation.many && value && typeof value === 'object' && !Array.isArray(value)) {
			// To-one relation: recurse into the object (no unwrapping)
			const targetCollection = getTargetCollection(relation, store);

			result[key] = targetCollection
				? unwrapNestedDataEnvelopes(value, targetCollection, store)
				: value;
		}
		else {
			// Non-relation field or null: pass through
			result[key] = value;
		}
	}

	return result as T;
}

/**
 * Type guard to check if value is a data envelope: { data: unknown[] }
 */
function isDataEnvelope(value: unknown): value is { data: unknown[] } {
	return (
		value !== null
		&& typeof value === 'object'
		&& 'data' in value
		&& Array.isArray((value as { data: unknown }).data)
	);
}

/**
 * Get the target collection from a normalized relation
 */
function getTargetCollection(
	relation: { to: Array<{ collection: string }> },
	store: Store,
): ResolvedCollection | undefined {
	const targetName = relation.to[0]?.collection;
	if (!targetName) return undefined;

	return store.$collections.find((c) => c.name === targetName);
}
